/**
 * LLM token usage chart — thin wrapper around TimeSeriesChart.
 *
 * @module components/TokenChart
 */

import React, { useMemo }  from 'react';
import TimeSeriesChart      from './TimeSeriesChart';
import * as constants       from '../constants';
import { useI18n }          from '../dataProviders/I18nProvider';

/**
 * Transforms raw token rows into a recharts-friendly array.
 *
 * @param {object[]} rows - Array of { bucket, inputTokens, outputTokens }.
 * @returns {object[]} Recharts data array.
 */
function transformData(rows) {
  return rows.map(row => ({
    bucket:       row.bucket,
    time:         new Date(row.bucket).getTime(),
    inputTokens:  row.inputTokens,
    outputTokens: row.outputTokens,
  }));
}

/**
 * Renders a single LLM token usage chart for one API key.
 *
 * @param {object}   props
 * @param {object[]} props.data              - Array of { bucket, inputTokens, outputTokens }.
 * @param {string}   props.apiKeyDescription - Description of the API key.
 * @param {string}   props.interval          - Time interval (hour/day/week/month).
 * @param {number}   props.rangeStart        - Epoch ms for x-axis domain start.
 * @param {number}   props.rangeEnd          - Epoch ms for x-axis domain end.
 * @param {boolean}  props.useLocalTime      - Whether to display in browser local time.
 * @returns {React.ReactElement}
 */
export default function TokenChart({ data, apiKeyDescription, interval,
                                     rangeStart, rangeEnd, useLocalTime }) {
  const { t }     = useI18n();
  const chartData = useMemo(() => transformData(data), [data]);

  return (
    <TimeSeriesChart
      data={chartData}
      title={t('dashboard.tokenChartTitle', { description: apiKeyDescription })}
      series={constants.TOKEN_TYPES}
      colors={constants.TOKEN_COLORS}
      legendFormatter={(value) => t(`dashboard.token.${value}`)}
      interval={interval}
      rangeStart={rangeStart}
      rangeEnd={rangeEnd}
      useLocalTime={useLocalTime}
    />
  );
}

export { transformData };
